import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, CheckCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

const PHASE_LABELS = {
  queued: "En cola…",
  uploading: "Subiendo archivo…",
  starting: "Iniciando…",
  parsing: "Leyendo archivo (CSV/XLSX)…",
  upserting: "Procesando leads…",
  archiving: "Archivando snapshot…",
  pruning: "Limpiando datos obsoletos…",
  done: "Listo",
};
function phaseLabel(phase) {
  return PHASE_LABELS[phase] || (phase ? `Fase: ${phase}` : "Procesando…");
}

export default function DataEntryPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [replaceMode, setReplaceMode] = useState(true);
  const [jobStatus, setJobStatus] = useState(null);  // { phase, current, total }

  const loadStats = async () => {
    try {
      const r = await api.get("/dashboard/summary", { params: { year: "Todos" } });
      setStats({ total: r.data.total, unmanaged: r.data.unmanaged_count });
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadStats(); }, []);

  const pollJob = async (jobId, toastId) => {
    // Polls /api/upload-jobs/{job_id} every 2s until done or error.
    while (true) {
      try {
        const r = await api.get(`/upload-jobs/${jobId}`);
        const d = r.data;
        setJobStatus({ phase: d.phase, current: d.progress_current || 0, total: d.progress_total || 0 });
        if (d.status === "done") {
          setResult(d.result || {});
          toast.success(`CSV cargado · ${(d.result?.total ?? 0).toLocaleString()} registros`, {
            id: toastId,
            description: "El dashboard CRM Overview ya refleja los datos.",
          });
          return;
        }
        if (d.status === "error") {
          const msg = d.error || "Error en el procesamiento";
          setError(msg);
          toast.error("Error al procesar CSV", { id: toastId, description: msg });
          return;
        }
      } catch (e) {
        // Transient errors during polling — keep trying for a few seconds
      }
      await new Promise((res) => setTimeout(res, 2000));
    }
  };

  const uploadCSV = async () => {
    if (!csvFile) {
      setError("Selecciona un archivo CSV o XLSX");
      toast.warning("Selecciona un archivo primero");
      return;
    }
    setError("");
    setResult(null);
    setJobStatus({ phase: "uploading", current: 0, total: 0 });
    setUploading(true);
    const fileName = csvFile.name;
    const toastId = toast.loading(`Subiendo ${fileName}…`);
    const fd = new FormData();
    fd.append("file", csvFile);
    try {
      const r = await api.post(`/leads/upload-csv?replace=${replaceMode}`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      // Async path: poll the job
      if (r.data?.job_id) {
        toast.loading(`Procesando ${fileName} en segundo plano…`, { id: toastId });
        await pollJob(r.data.job_id, toastId);
      } else {
        // Legacy synchronous path (shouldn't happen with new backend)
        setResult(r.data);
        toast.success(`CSV cargado · ${r.data.total ?? 0} registros`, { id: toastId });
      }
      setCsvFile(null);
      loadStats();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Error en la carga";
      setError(msg);
      toast.error("Error al subir CSV", { id: toastId, description: msg });
    } finally {
      setUploading(false);
      setJobStatus(null);
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto p-5">
      <header className="surface flex justify-between items-center gap-4 p-5 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/" className="btn flex items-center gap-2" data-testid="back-to-dashboard"><ArrowLeft size={16}/> Dashboard</Link>
          <div>
            <h1 className="text-2xl font-extrabold font-display">Carga de Datos desde PowerBI</h1>
            <p className="text-sm text-gray-500 font-semibold mt-1">Sube el CSV exportado de tu reporte PowerBI. La carga reemplaza todos los leads existentes.</p>
          </div>
        </div>
        <button className="btn" onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn">Salir</button>
      </header>

      {stats && (
        <section className="grid gap-3 mb-5" style={{ gridTemplateColumns: "1fr 1fr" }}>
          <div className="surface p-5" data-testid="stat-total">
            <div className="label-up mb-2">Leads cargados actualmente</div>
            <div className="font-display text-3xl font-extrabold">{Number(stats.total).toLocaleString("es-DO")}</div>
            <div className="muted-small mt-1">Datos en vivo en MongoDB</div>
          </div>
          <div className="surface p-5" data-testid="stat-unmanaged">
            <div className="label-up mb-2">Sin gestionar (Nuevo · No Contactado · Intentando)</div>
            <div className="font-display text-3xl font-extrabold text-amber-700">{Number(stats.unmanaged).toLocaleString("es-DO")}</div>
            <div className="muted-small mt-1">Requieren acción inmediata</div>
          </div>
        </section>
      )}

      <section className="surface p-8" data-testid="upload-panel">
        <h2 className="text-xl font-extrabold font-display mb-2">Subir CSV</h2>
        <p className="text-sm text-gray-500 font-semibold mb-6">
          Exporta desde PowerBI / Dynamics tu tabla de leads en formato CSV (delimitador <code>;</code>).
          La carga es <strong>idempotente por <code>(No modificar) Cliente potencial</code></strong>: los leads existentes se actualizan, los nuevos se insertan.
        </p>

        <div
          className={"dropzone" + (csvFile ? " drag" : "")}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setCsvFile(f); }}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => document.getElementById("csv-input").click()}
          data-testid="csv-dropzone"
        >
          <Upload size={36} style={{ margin: "0 auto 10px", display: "block", color: "#6b7280" }}/>
          <div className="font-bold text-lg">{csvFile ? csvFile.name : "Arrastra tu CSV/XLSX o haz clic para seleccionar"}</div>
          <div className="muted-small mt-2">CSV (separador <code>;</code>, latin-1 o UTF-8) o XLSX (Excel) · máximo recomendado 500K filas</div>
          <input id="csv-input" type="file" accept=".csv,.xlsx" hidden onChange={(e) => setCsvFile(e.target.files?.[0])} data-testid="csv-input"/>
        </div>

        <div className="flex justify-between items-center mt-5 flex-wrap gap-3">
          <div className="muted-small" style={{ maxWidth: 540 }}>
            Columnas esperadas (mínimas): <code>(No modificar) Cliente potencial</code>, <code>Estado</code>, <code>Marca de Interés</code>, <code>Unidad de negocio propietaria</code>.
            <br/>Opcionales: <code>Fecha de creación</code>, <code>Fecha de modificación</code>, <code>Owner</code>, <code>Origen</code>, <code>Razón para el estado</code>, <code>Modelo</code>, <code>Vía de primer contacto</code>, <code>Campaña</code>, <code>Cumpleaños…</code>.
          </div>
          <button className="btn primary flex items-center gap-2" onClick={uploadCSV} disabled={!csvFile || uploading} data-testid="csv-upload-btn">
            {uploading ? <span className="spinner"></span> : <Upload size={16}/>}
            {uploading ? "Procesando…" : (replaceMode ? "Subir y Reemplazar Datos" : "Subir y Añadir Datos")}
          </button>
        </div>

        {/* Replace vs Append toggle */}
        <div
          className="mt-4 p-4 rounded-xl border"
          style={{ borderColor: "#e5e7eb", background: "#fafbfc" }}
        >
          <div className="font-bold mb-3" style={{ fontSize: 13, color: "#374151" }}>Modo de carga</div>
          <div className="flex flex-col gap-2">
            <label className="flex items-start gap-2 cursor-pointer" data-testid="upload-mode-replace">
              <input
                type="radio"
                name="upload-mode"
                checked={replaceMode}
                onChange={() => setReplaceMode(true)}
                style={{ marginTop: 3 }}
              />
              <div>
                <div className="font-semibold" style={{ fontSize: 13 }}>Reemplazar todos los datos</div>
                <div className="muted-small">
                  El CSV es la <strong>fuente completa</strong>. Leads que NO aparecen en el archivo se eliminan.
                  Los datos no se borran hasta que la carga termine OK (a prueba de timeouts).
                </div>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer" data-testid="upload-mode-append">
              <input
                type="radio"
                name="upload-mode"
                checked={!replaceMode}
                onChange={() => setReplaceMode(false)}
                style={{ marginTop: 3 }}
              />
              <div>
                <div className="font-semibold" style={{ fontSize: 13 }}>Añadir / Actualizar (append)</div>
                <div className="muted-small">
                  Inserta leads nuevos y actualiza los existentes por <code>lead_id</code>.
                  <strong> Nada se elimina.</strong> Útil para cargas parciales o incrementales.
                </div>
              </div>
            </label>
          </div>
        </div>

        {error && (
          <div className="mt-5 p-4 rounded-xl border border-red-200 bg-red-50 text-red-800 font-semibold flex items-start gap-2" data-testid="upload-error">
            <AlertTriangle size={20} className="flex-shrink-0 mt-0.5"/>
            <div>{error}</div>
          </div>
        )}
        {jobStatus && uploading && (
          <div className="mt-5 p-4 rounded-xl border" style={{ borderColor: "#dbeafe", background: "#eff6ff" }} data-testid="upload-progress">
            <div className="flex justify-between items-center mb-2">
              <div className="font-bold" style={{ color: "#1e40af", fontSize: 14 }}>
                {phaseLabel(jobStatus.phase)}
              </div>
              <div className="muted-small font-mono" style={{ color: "#1e40af" }}>
                {jobStatus.total > 0
                  ? `${jobStatus.current.toLocaleString()} / ${jobStatus.total.toLocaleString()}`
                  : "…"}
              </div>
            </div>
            <div style={{ height: 8, background: "#dbeafe", borderRadius: 999, overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width: jobStatus.total > 0
                  ? `${Math.min(100, (jobStatus.current / jobStatus.total) * 100)}%`
                  : "10%",
                background: "linear-gradient(90deg, #3b82f6, #2563eb)",
                transition: "width 0.4s ease",
                animation: jobStatus.total === 0 ? "sdmSpin 1s linear infinite" : undefined,
              }}/>
            </div>
            <div className="muted-small mt-2" style={{ color: "#1e40af" }}>
              Puedes cerrar esta pantalla — el proceso continuará en segundo plano. El dashboard se actualizará automáticamente al terminar.
            </div>
          </div>
        )}
        {result && (
          <div className="mt-5 p-4 rounded-xl border border-green-200 bg-green-50 text-green-900 font-semibold flex items-start gap-2" data-testid="upload-success">
            <CheckCircle size={20} className="flex-shrink-0 mt-0.5"/>
            <div>
              ¡Carga exitosa! Procesados <strong>{result.total}</strong> registros. El dashboard ya refleja los nuevos datos.
            </div>
          </div>
        )}
      </section>

      <section className="surface p-6 mt-5" data-testid="powerbi-info">
        <h3 className="font-display font-extrabold text-lg mb-2">Conectar a Power BI (lectura)</h3>
        <p className="text-sm text-gray-600 font-semibold mb-3">
          Si quieres consumir estos datos desde PowerBI Desktop, usa <strong>Obtener Datos → Web</strong> y pega:
        </p>
        <code className="block bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs break-all">
          {`${process.env.REACT_APP_BACKEND_URL}/api/powerbi/feed?token=YOUR_FEED_TOKEN`}
        </code>
        <p className="text-xs text-gray-500 mt-2 font-semibold">Solicita el token a IT (primeros 32 caracteres del JWT_SECRET del servidor).</p>
      </section>
    </div>
  );
}
